import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, Select, OutlinedInput, MenuItem, Chip, Box, Typography, IconButton, Checkbox, Grid, Popover, TextField, Tooltip, FormControlLabel } from '@mui/material';
import { Close, Clear, Comment } from '@mui/icons-material';

import useReactHookFormStyles from '../../../../../custom-components/form-components/ReactHookForm.styles';
import FormInput from '../../input/FormInput';
import BooleanSelect from '../../boolean/BooleanSelect';
import { createRapidTestStandardsSchema } from './RapidTestStandardsInput.schema';
import RapidTestComponents from './components';

const NoteButton = ({ note, onNoteChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const hasNote = !!note;

    const handleChange = (e) => {
        onNoteChange(e.target.value || null);
    };

    const handleClear = () => {
        onNoteChange(null);
        setAnchorEl(null);
    };

    return (
        <>
            <Tooltip title={hasNote ? note : 'Add note'} arrow>
                <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5, color: hasNote ? 'primary.main' : 'text.disabled' }}>
                    <Comment fontSize="small" />
                </IconButton>
            </Tooltip>
            <Popover open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Box sx={{ p: 2, width: 280 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Add a note
                    </Typography>
                    <TextField value={note || ''} onChange={handleChange} size="small" fullWidth multiline minRows={2} placeholder="Enter note..." autoFocus />
                    {hasNote && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <IconButton size="small" onClick={handleClear} sx={{ color: 'text.secondary' }} title="Clear note">
                                <Clear fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Popover>
        </>
    );
};

const RapidTestStandardsInput = ({ name, props: { label = 'Rapid Test Standards', required, validationSchema } }) => {
    const classes = useReactHookFormStyles();

    const {
        control,
        formState: { errors },
        watch,
        clearErrors,
        setError,
    } = useFormContext();

    const currentValues = watch(name) || {};
    // Stringify to detect nested object changes
    const currentValuesJson = JSON.stringify(currentValues);

    useEffect(() => {
        if (Object.keys(currentValues).length > 0) {
            const schema = validationSchema || createRapidTestStandardsSchema();

            // Clear existing errors for this field first
            clearErrors(name);

            schema
                .validate(currentValues, { abortEarly: false })
                .then(() => {
                    // Validation passed, errors already cleared
                })
                .catch((validationError) => {
                    if (validationError.inner) {
                        validationError.inner.forEach((err) => {
                            const fieldPath = `${name}.${err.path}`;
                            setError(fieldPath, {
                                type: 'validation',
                                message: err.message,
                            });
                        });
                    }
                });
        } else {
            clearErrors(name);
        }
    }, [currentValuesJson, name, validationSchema, clearErrors, setError]);

    const nestedName = name.split('.');
    const error = errors[name] || (nestedName.length && nestedName.reduce((acc, key) => acc?.[key], errors));

    const getFieldError = (componentKey, field) => {
        const fieldPath = `${name}.${componentKey}.${field}`;
        return fieldPath.split('.').reduce((acc, key) => acc?.[key], errors);
    };

    const renderNumericRangeField = (component, handleDelete, handleRequiredChange, handleNoteChange, fieldValue) => {
        const minError = getFieldError(component.key, 'min');
        const maxError = getFieldError(component.key, 'max');
        const isRequired = fieldValue?.required !== false;

        return (
            <>
                {/* Header row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {component.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <NoteButton note={fieldValue?.note} onNoteChange={(note) => handleNoteChange(component.key, note)} />
                        <Checkbox checked={isRequired} onChange={(e) => handleRequiredChange(component.key, e.target.checked)} size="small" sx={{ p: 0.5 }} title="Required" />
                        <IconButton size="small" onClick={() => handleDelete(component.key)} sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'error.main' } }} title={`Remove ${component.label}`}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {/* Input row */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormInput
                        name={`${name}.${component.key}.min`}
                        props={{
                            label: 'Min',
                            type: 'number',
                            variant: 'outlined',
                            size: 'small',
                            valueAsNumber: true,
                            placeholder: 'None',
                            error: !!minError,
                            helperText: minError?.message,
                            fullWidth: true,
                            inputProps: { step: 0.01 },
                            sx: { bgcolor: 'background.paper' },
                        }}
                    />
                    <FormInput
                        name={`${name}.${component.key}.max`}
                        props={{
                            label: 'Max',
                            type: 'number',
                            variant: 'outlined',
                            size: 'small',
                            valueAsNumber: true,
                            placeholder: 'None',
                            error: !!maxError,
                            helperText: maxError?.message,
                            fullWidth: true,
                            inputProps: { step: 0.01 },
                            sx: { bgcolor: 'background.paper' },
                        }}
                    />
                </Box>
            </>
        );
    };

    const renderBooleanField = (component, handleDelete, handleRequiredChange, handleNoteChange, fieldValue) => {
        const fieldError = getFieldError(component.key, 'required_value');
        const isRequired = fieldValue?.required !== false;

        return (
            <>
                {/* Header row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {component.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <NoteButton note={fieldValue?.note} onNoteChange={(note) => handleNoteChange(component.key, note)} />
                        <Checkbox checked={isRequired} onChange={(e) => handleRequiredChange(component.key, e.target.checked)} size="small" sx={{ p: 0.5 }} title="Required" />
                        <IconButton size="small" onClick={() => handleDelete(component.key)} sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'error.main' } }} title={`Remove ${component.label}`}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {/* Input row */}
                <BooleanSelect
                    name={`${name}.${component.key}.required_value`}
                    props={{
                        label: 'Pass Condition',
                        variant: 'outlined',
                        size: 'small',
                        placeholder: 'Any value',
                        error: !!fieldError,
                        helperText: fieldError?.message,
                        fullWidth: true,
                        optionMap: {
                            true: component.trueLabel ? `Must be ${component.trueLabel}` : 'Must be Positive',
                            false: component.falseLabel ? `Must be ${component.falseLabel}` : 'Must be Negative',
                            empty: 'Any value',
                        },
                        sx: { bgcolor: 'background.paper' },
                    }}
                />
            </>
        );
    };

    return (
        <FormControl className={classes.formController} error={!!error}>
            <Controller
                name={name}
                control={control}
                defaultValue={{}}
                render={({ field }) => {
                    const selectedComponents = Object.keys(field.value || {});
                    const selectedComponentObjects = RapidTestComponents.filter((comp) => selectedComponents.includes(comp.key));

                    const handleComponentSelection = (event) => {
                        const { value } = event.target;
                        const newValue = {};

                        value.forEach((componentKey) => {
                            const component = RapidTestComponents.find((c) => c.key === componentKey);
                            if (component) {
                                // Keep existing value if present, otherwise create default with required: true
                                newValue[componentKey] = field.value?.[componentKey] || (component.type === 'numeric' ? { min: null, max: null, required: true, unlimited_retests: true, max_retests: 0, fails_rapid_test: true } : { required_value: null, required: true, unlimited_retests: true, max_retests: 0, fails_rapid_test: true });
                            }
                        });

                        field.onChange(newValue);
                    };

                    const handleDeleteComponent = (componentKey) => {
                        const newValue = { ...field.value };
                        delete newValue[componentKey];
                        field.onChange(newValue);
                    };

                    const handleRequiredChange = (componentKey, isRequired) => {
                        const current = field.value ?? {};
                        const currentComponent = current[componentKey] ?? {};
                        field.onChange({
                            ...current,
                            [componentKey]: {
                                ...currentComponent,
                                required: isRequired,
                            },
                        });
                    };

                    const handleNoteChange = (componentKey, note) => {
                        const current = field.value ?? {};
                        const currentComponent = current[componentKey] ?? {};
                        field.onChange({
                            ...current,
                            [componentKey]: {
                                ...currentComponent,
                                note,
                            },
                        });
                    };

                    const handleClearAll = (event) => {
                        event.stopPropagation();
                        field.onChange({});
                    };

                    const setValue = (componentKey, key, value) => {
                        const current = field.value ?? {};
                        const currentComponent = current[componentKey] ?? {};
                        field.onChange({
                            ...current,
                            [componentKey]: {
                                ...currentComponent,
                                [key]: value,
                                ...(key === 'unlimited_retests' && !!value && { max_retests: 0 }),
                            },
                        });
                    };

                    return (
                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {label}
                                {required && <span style={{ color: 'red' }}> *</span>}
                            </Typography>

                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <Select
                                    multiple
                                    value={selectedComponents}
                                    onChange={handleComponentSelection}
                                    input={<OutlinedInput />}
                                    displayEmpty
                                    renderValue={(selected) => {
                                        if (selected.length === 0) {
                                            return <em style={{ color: '#999' }}>Select Components to Test</em>;
                                        }
                                        return (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 0.5,
                                                    overflow: 'hidden',
                                                    width: '100%',
                                                }}
                                            >
                                                {selected.map((value) => {
                                                    const component = RapidTestComponents.find((comp) => comp.key === value);
                                                    return (
                                                        <Chip
                                                            key={value}
                                                            label={component?.label || value}
                                                            size="small"
                                                            onDelete={(event) => {
                                                                event.stopPropagation();
                                                                handleDeleteComponent(value);
                                                            }}
                                                            deleteIcon={<Close fontSize="small" />}
                                                            sx={{
                                                                maxWidth: '150px',
                                                                '& .MuiChip-label': {
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                },
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        );
                                    }}
                                    endAdornment={
                                        selectedComponents.length > 0 && (
                                            <IconButton
                                                onClick={handleClearAll}
                                                size="small"
                                                title="Clear All"
                                                sx={{
                                                    color: 'text.secondary',
                                                    mr: 1,
                                                    position: 'absolute',
                                                    right: 32,
                                                    zIndex: 1,
                                                }}
                                            >
                                                <Clear />
                                            </IconButton>
                                        )
                                    }
                                    sx={{
                                        '& .MuiSelect-select': {
                                            minHeight: '40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            overflow: 'hidden',
                                            paddingRight: selectedComponents.length > 0 ? '60px !important' : undefined,
                                        },
                                    }}
                                >
                                    {RapidTestComponents.map((component) => (
                                        <MenuItem key={component.key} value={component.key}>
                                            {component.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {selectedComponentObjects.length > 0 && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                            Configure Standards
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Checkbox checked size="small" sx={{ p: 0, cursor: 'default' }} disableRipple />
                                            <Typography variant="caption" color="text.secondary">
                                                = Required
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Grid container spacing={2}>
                                        {selectedComponentObjects.map((component) => (
                                            <Grid item xs={12} md={6} key={component.key}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        bgcolor: 'grey.50',
                                                        borderRadius: 1,
                                                        border: 1,
                                                        borderColor: 'grey.200',
                                                        height: '100%',
                                                    }}
                                                >
                                                    {component.type === 'numeric' ? renderNumericRangeField(component, handleDeleteComponent, handleRequiredChange, handleNoteChange, field.value?.[component.key]) : renderBooleanField(component, handleDeleteComponent, handleRequiredChange, handleNoteChange, field.value?.[component.key])}
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
                                                        <FormInput
                                                            name={`${name}.${component.key}.max_retests`}
                                                            props={{
                                                                label: '# Retests',
                                                                type: 'number',
                                                                variant: 'outlined',
                                                                size: 'small',
                                                                valueAsNumber: true,
                                                                placeholder: '0',
                                                                fullWidth: true,
                                                                disabled: !!field.value?.[component.key]?.unlimited_retests,
                                                                inputProps: { min: 0, step: 1 },
                                                                sx: { bgcolor: 'background.paper' },
                                                            }}
                                                        />
                                                        <Box sx={{ height: 40, display: 'flex', alignItems: 'center' }}>
                                                            <FormControlLabel
                                                                control={<Checkbox checked={!!field.value?.[component.key]?.unlimited_retests} onChange={(e) => setValue(component.key, 'unlimited_retests', e.target.checked)} size="small" />}
                                                                label="Unlimited?"
                                                                slotProps={{ typography: { variant: 'caption', noWrap: true } }}
                                                                sx={{ whiteSpace: 'nowrap' }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                    <Box>
                                                        <FormControlLabel
                                                            control={<Checkbox checked={!!field.value?.[component.key]?.fails_rapid_test} onChange={(e) => setValue(component.key, 'fails_rapid_test', e.target.checked)} size="small" />}
                                                            label="Fails test?"
                                                            slotProps={{ typography: { variant: 'caption', noWrap: true } }}
                                                            sx={{ whiteSpace: 'nowrap' }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}
                        </Box>
                    );
                }}
            />
        </FormControl>
    );
};

export default RapidTestStandardsInput;
